/// <reference types="google.maps" />
import React from 'react';
import { useForm } from 'react-hook-form';
import { usePushEvents } from '../../../../hooks/PushEvents';
import {
    loginPopupAddressScreenNext,
    loginPopupAddressScreenShown,
} from '../../../../hooks/PushEvents/clevertapEvents';
import { useGoogleAutocomplete } from '../../../Wrappers/useGoogleAutocomplete';
import { getAddressComponents } from '../../../../utils';

export type AddressData = {
    street: string;
    city: string;
    state: string;
    zip: string;
    countryCode: string;
};

type AddressFormValues = {
    zip: string;
    countryCode: string;
};

const getOptionLabel = (
    option: google.maps.places.AutocompletePrediction,
): string => {
    const mainText = option.structured_formatting.main_text;
    const secondaryText = option.structured_formatting.secondary_text;
    return secondaryText ? `${mainText}, ${secondaryText}` : mainText;
};

export const useAddressStep = (
    onAddressSubmit: (address: AddressData) => void,
    defaultAddress?: AddressData,
) => {
    const { pushEvent } = usePushEvents();
    const [street, setStreet] = React.useState(defaultAddress?.street ?? '');
    const [city, setCity] = React.useState(defaultAddress?.city ?? '');
    const [state, setState] = React.useState(defaultAddress?.state ?? '');
    const [streetError, setStreetError] = React.useState<string | undefined>();
    const [predictions, setPredictions] = React.useState<
        google.maps.places.AutocompletePrediction[]
    >([]);
    const [showSuggestions, setShowSuggestions] = React.useState(false);

    const { makeLookupRequest, makeGeocoderRequest } = useGoogleAutocomplete();

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<AddressFormValues>({
        values: {
            zip: defaultAddress?.zip ?? '',
            countryCode: defaultAddress?.countryCode ?? 'US',
        },
    });

    React.useEffect(() => {
        if (defaultAddress?.street !== undefined) {
            setStreet(defaultAddress.street);
        }
    }, [defaultAddress?.street]);

    const zipValue = watch('zip');
    const countryValue = watch('countryCode');

    React.useEffect(() => {
        pushEvent(loginPopupAddressScreenShown);
        // run only once when component is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStreetChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setStreet(value);
            setCity('');
            setState('');
            setStreetError(undefined);
            if (value.length > 2) {
                makeLookupRequest(value, (results) => {
                    setPredictions(results);
                    setShowSuggestions(true);
                });
            } else {
                setShowSuggestions(false);
            }
        },
        [makeLookupRequest],
    );

    const handleSuggestionSelect = React.useCallback(
        (prediction: google.maps.places.AutocompletePrediction) => {
            const label = getOptionLabel(prediction);
            const address = label.split(',')[0];
            setStreet(address);
            setShowSuggestions(false);
            makeGeocoderRequest(prediction.place_id, (place) => {
                const { zip, city, state, countryCode } = getAddressComponents(
                    place.address_components,
                );
                setCity(city);
                setState(state);
                setValue('zip', zip);
                if (countryCode) setValue('countryCode', countryCode);
            });
        },
        [makeGeocoderRequest, setValue],
    );

    const hideSuggestions = React.useCallback(() => {
        setTimeout(() => setShowSuggestions(false), 150);
    }, []);

    const onSubmit = handleSubmit((data) => {
        if (street.length <= 2) {
            setStreetError('*Please enter a valid street address');
            return;
        }
        pushEvent(loginPopupAddressScreenNext);
        onAddressSubmit({
            street,
            city,
            state,
            zip: data.zip,
            countryCode: data.countryCode,
        });
    });

    return {
        register,
        errors,
        street,
        streetError,
        zipValue,
        countryValue,
        predictions,
        showSuggestions,
        handleStreetChange,
        handleSuggestionSelect,
        hideSuggestions,
        onSubmit,
    };
};
